import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useAuth } from 'context/AuthContext';
import {
    BringzzIcon, BringzzText, BringzzBoxSlider, BringzzPageContainer
} from "@bringzz/components";
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import { useDebounce } from '@bringzz/hooks';
import ProductTab from './components/ProductTab';
import PharmacyTab from './components/PharmacyTab';
import ArticleTab from './components/ArticleTab';
import ManufacturerTab from './components/ManufacturerTab';
import OrderTab from './components/OrderTab';
import BookmarksTab from './components/BookmarksTab';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const SearchModal = ({ isOpen, onClose, companyId = null, companyName = null }) => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const { searchHistory, saveSearchHistory, deleteSearchHistory, clearSearchHistory } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
    const [companyProducts, setCompanyProducts] = useState([]);
    const [isCompanyMode, setIsCompanyMode] = useState(false);
    const [currentCompanyId, setCurrentCompanyId] = useState(null);
    const [filteredResults, setFilteredResults] = useState({});
    const [expandedSections, setExpandedSections] = useState({});
    const [showHistory, setShowHistory] = useState(false);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [pages, setPages] = useState({ products: 1, pharmacies: 1, posts: 1, companies: 1 });
    const [hasMore, setHasMore] = useState({ products: true, pharmacies: true, posts: true, companies: true });
    const [tabPages, setTabPages] = useState({ products: 1, pharmacies: 1, posts: 1, companies: 1 });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const searchContainerRef = useRef(null);
    const observer = useRef();
    const isSearchingRef = useRef(false);
    const { sendRequest, loading } = useRequest();
    const debouncedSearchQuery = useDebounce(searchQuery, 400);
    const debouncedSuggestionQuery = useDebounce(searchQuery, 400);

    const filterTypes = [
        t('component.search.general.all'),
        t('component.search.general.product'),
        t('component.search.general.article'),
        t('component.search.general.pharmacies'),
        t('component.search.general.manufacturer'),
        t('component.search.general.bookmarks'),
        t('component.search.general.order')
    ];

    const searchCompanyProducts = async (companyId, resetPages = false) => {
        try {
            const currentPage = resetPages ? 1 : pages.products;

            const response = await sendRequest({
                method: 'GET',
                url: '/module/search/getProductsByCompany',
                params: {
                    companyId: companyId,
                    page: currentPage,
                    limit: 20
                }
            });

            if (response.status === 'success' && response.data) {
                setCompanyProducts(prevProducts => {
                    if (resetPages) {
                        return response.data.products || [];
                    } else {
                        return [...prevProducts, ...(response.data.products || [])];
                    }
                });

                setHasMore({
                    ...hasMore,
                    products: (response.data.products || []).length === 20
                });

                if (resetPages) {
                    setPages({ ...pages, products: currentPage });
                }
            }
        } catch (error) {
            console.error('Company products search error:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const searchItems = async (query, resetPages = false, newPages = null) => {
        if (!query.trim()) {
            setFilteredResults({});
            return;
        }

        try {
            const currentPages = resetPages ? { products: 1, pharmacies: 1, posts: 1, companies: 1 } : (newPages || pages);

            const response = await sendRequest({
                method: 'GET',
                url: '/module/search/findItems',
                params: {
                    query: query.trim(),
                    productsPage: currentPages.products,
                    pharmaciesPage: currentPages.pharmacies,
                    postsPage: currentPages.posts,
                    companiesPage: currentPages.companies,
                    limit: 20,
                    grouped: 1
                }
            });

            if (response.status === 'success' && response.data) {
                setFilteredResults(prevResults => {
                    if (resetPages) {
                        return {
                            products: response.data.products?.data || [],
                            pharmacies: response.data.pharmacies?.data || [],
                            posts: response.data.posts?.data || [],
                            companies: response.data.companies?.data || [],
                            productCount: response.data.products?.productCount || 0,
                            pharmacyCount: response.data.pharmacies?.pharmacyCount || 0,
                            postCount: response.data.posts?.postCount || 0,
                            companyCount: response.data.companies?.companyCount || 0
                        };
                    } else {
                        // Deduplicate by ID to prevent duplicates
                        const deduplicateById = (existing, newItems) => {
                            const existingIds = new Set(existing.map(item => item.id));
                            const uniqueNew = newItems.filter(item => !existingIds.has(item.id));
                            return [...existing, ...uniqueNew];
                        };

                        return {
                            products: deduplicateById(prevResults.products || [], response.data.products?.data || []),
                            pharmacies: deduplicateById(prevResults.pharmacies || [], response.data.pharmacies?.data || []),
                            posts: deduplicateById(prevResults.posts || [], response.data.posts?.data || []),
                            companies: deduplicateById(prevResults.companies || [], response.data.companies?.data || []),
                            productCount: response.data.products?.productCount || prevResults.productCount || 0,
                            pharmacyCount: response.data.pharmacies?.pharmacyCount || prevResults.pharmacyCount || 0,
                            postCount: response.data.posts?.postCount || prevResults.postCount || 0,
                            companyCount: response.data.companies?.companyCount || prevResults.companyCount || 0
                        };
                    }
                });

                setHasMore({
                    products: (response.data.products?.data || []).length === 20,
                    pharmacies: (response.data.pharmacies?.data || []).length === 20,
                    posts: (response.data.posts?.data || []).length === 20,
                    companies: (response.data.companies?.data || []).length === 20
                });

                if (resetPages || newPages) {
                    setPages(currentPages);
                }
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const fetchSuggestions = async (query) => {
        if (!query.trim() || query.length < 1 || isCompanyMode || isSearchingRef.current) {
            setSearchSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        try {
            const response = await sendRequest({
                method: 'GET',
                url: '/module/search/findItems',
                params: {
                    query: query.trim(),
                    limit: 5
                }
            });

            if (response.status === 'success' && response.data && !isSearchingRef.current) {
                const suggestions = new Set();

                suggestions.add(query.trim());

                response.data.products?.data?.slice(0, 3).forEach(product => {
                    if (product.productName) suggestions.add(product.productName);
                });

                response.data.pharmacies?.data?.slice(0, 2).forEach(pharmacy => {
                    if (pharmacy.name) suggestions.add(pharmacy.name);
                });

                response.data.posts?.data?.slice(0, 2).forEach(post => {
                    if (post.headline) suggestions.add(post.headline);
                });

                setSearchSuggestions(Array.from(suggestions).slice(0, 5));
                setShowSuggestions(true);
            }
        } catch (error) {
            console.error('Suggestion error:', error);
        }
    };

    useEffect(() => {
        setShowHistory(!searchQuery.trim() && !isCompanyMode);
        if (!searchQuery.trim() || isCompanyMode) {
            setShowSuggestions(false);
            setSearchSuggestions([]);
        }
    }, [searchQuery, isCompanyMode]);

    useEffect(() => {
        if (debouncedSuggestionQuery.trim() && debouncedSuggestionQuery.length >= 1 && !isCompanyMode) {
            fetchSuggestions(debouncedSuggestionQuery);
        }
    }, [debouncedSuggestionQuery, isCompanyMode]);

    useEffect(() => {
        if (companyId && companyName) {
            setIsCompanyMode(true);
            setSelectedFilterIndex(1);
            setSearchQuery(companyName);
            searchCompanyProducts(companyId, true);
        }
    }, [companyId, companyName]);

    const performSearch = (query) => {
        if (query.trim() && !isCompanyMode) {
            setPages({ products: 1, pharmacies: 1, posts: 1, companies: 1 });
            setTabPages({ products: 1, pharmacies: 1, posts: 1, companies: 1 });
            setShowSuggestions(false);
            searchItems(query.trim(), true);
        }
    };

    const lastElementRef = useCallback(node => {
        if (loading || isLoadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && debouncedSearchQuery.trim()) {
                let shouldLoadMore = false;
                let newPages = { ...pages };

                if (selectedFilterIndex === 1 && hasMore.products) { // Product
                    newPages.products = tabPages.products + 1;
                    shouldLoadMore = true;
                    if (isCompanyMode && (companyId || currentCompanyId)) {
                        setIsLoadingMore(true);
                        setTabPages(prev => ({ ...prev, products: newPages.products }));
                        setPages(newPages);
                        setTimeout(() => {
                            searchCompanyProducts(companyId || currentCompanyId, false);
                        }, 0);
                        return;
                    }
                } else if (selectedFilterIndex === 3 && hasMore.pharmacies) { // Pharmacies
                    newPages.pharmacies = tabPages.pharmacies + 1;
                    shouldLoadMore = true;
                } else if (selectedFilterIndex === 2 && hasMore.posts) { // Article
                    newPages.posts = tabPages.posts + 1;
                    shouldLoadMore = true;
                } else if (selectedFilterIndex === 4 && hasMore.companies) { // Manufacturer
                    newPages.companies = tabPages.companies + 1;
                    shouldLoadMore = true;
                }

                if (shouldLoadMore) {
                    setIsLoadingMore(true);
                    setTabPages(prev => ({
                        ...prev,
                        products: selectedFilterIndex === 1 ? newPages.products : prev.products,
                        pharmacies: selectedFilterIndex === 3 ? newPages.pharmacies : prev.pharmacies,
                        posts: selectedFilterIndex === 2 ? newPages.posts : prev.posts,
                        companies: selectedFilterIndex === 4 ? newPages.companies : prev.companies
                    }));
                    setTimeout(() => {
                        searchItems(debouncedSearchQuery, false, newPages);
                    }, 0);
                }
            }
        }, { threshold: 0.1 });
        if (node) observer.current.observe(node);
    }, [loading, isLoadingMore, hasMore, pages, tabPages, debouncedSearchQuery, selectedFilterIndex, isCompanyMode, companyId, currentCompanyId]);

    const toggleBookmark = (id, type, section) => {
        // Bookmark toggle functionality
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getDisplayItems = (items, section) => {
        if (!items) return [];
        const isExpanded = expandedSections[section];
        return isExpanded ? items : items.slice(0, 5);
    };

    const handleItemClick = async (item, type) => {
        if (type === 'product') {
            if (item.integrationId) {
                navigate(`/product/details/${item.integrationId}`);
                onClose();
            } else {
                toast.error(t('component.search.general.product_not_registered'));
            }
        } else if (type === 'pharmacy') {
            navigate(`/pharmacy/profile/${item.integrationId}`);
            onClose();
        } else if (type === 'post') {
            navigate(`/post/details/${item.integrationId}`);
            onClose();
        } else if (type === 'manufacturer') {
            // Manufacturer has no detail page available
        } else if (type === 'order') {
            if (item.orderIntegrationId) {
                navigate(`/order/details/${item.orderIntegrationId}`);
                onClose();
            }
        }
    };

    const handleSuggestionClick = (suggestion) => {
        if (!isCompanyMode) {
            isSearchingRef.current = true;
            setShowSuggestions(false);
            setSearchSuggestions([]);
            setFilteredResults({});
            saveSearchHistory(suggestion.trim());
            setSearchQuery(suggestion);
            searchItems(suggestion.trim(), true);
            setTimeout(() => {
                isSearchingRef.current = false;
            }, 600);
        }
    };

    const handleSearchSubmit = () => {
        if (searchQuery.trim() && !isCompanyMode) {
            saveSearchHistory(searchQuery.trim());
            performSearch(searchQuery);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideSearch = searchContainerRef.current && !searchContainerRef.current.contains(event.target);
            const isOnSuggestion = event.target.closest('.suggestions-container');

            if (isOutsideSearch && !isOnSuggestion) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const transformedProducts = filteredResults.products?.map(p => ({
        id: p.id,
        name: p.groupName || p.productName,
        image: p.productImage,
        price: p.price ? `€${p.price}` : null,
        marked: p.isBookmarked,
        integrationId: p.integrationId,
        formLabel: p.formLabel || '',
        variantCount: p.variantCount || 1
    })) || [];

    const transformedPharmacies = filteredResults.pharmacies?.map(p => ({
        id: p.id,
        name: p.name,
        telefon: p.telefon,
        image: p.pharmacyPic,
        city: p.city,
        marked: p.isBookmarked,
        integrationId: p.integrationId
    })) || [];

    const transformedPosts = filteredResults.posts?.map(p => ({
        id: p.id,
        title: p.headline,
        author: `${p.user_first_name || ''} ${p.user_last_name || ''}`.trim(),
        authorImage: p.user_pic,
        postImage: p.images?.[0]?.content,
        publishDate: new Date().toLocaleDateString(),
        integrationId: p.integrationId
    })) || [];

    const transformedManufacturers = filteredResults.companies?.map(c => ({
        id: c.id,
        legal_name: c.legalName
    })) || [];

    const productTab = ProductTab({
        products: transformedProducts,
        isAllCategory: selectedFilterIndex === 0,
        onItemClick: handleItemClick,
        toggleBookmark
    });
    productTab.products = transformedProducts;
    const { renderProductItem } = productTab;

    const pharmacyTab = PharmacyTab({
        pharmacies: transformedPharmacies,
        isAllCategory: selectedFilterIndex === 0,
        onItemClick: handleItemClick,
        toggleBookmark
    });
    pharmacyTab.pharmacies = transformedPharmacies;
    const { renderPharmacyItem } = pharmacyTab;

    const articleTab = ArticleTab({
        posts: transformedPosts,
        isAllCategory: selectedFilterIndex === 0,
        onItemClick: handleItemClick
    });
    articleTab.posts = transformedPosts;
    const { renderPostItem } = articleTab;

    const handleManufacturerProductsClick = (manufacturerId, manufacturerName) => {
        setIsCompanyMode(true);
        setSelectedFilterIndex(1);
        setSearchQuery(manufacturerName);
        setCurrentCompanyId(manufacturerId);
        searchCompanyProducts(manufacturerId, true);
    };

    const manufacturerTab = ManufacturerTab({
        manufacturers: transformedManufacturers,
        isAllCategory: selectedFilterIndex === 0,
        onItemClick: handleItemClick,
        onManufacturerProductsClick: handleManufacturerProductsClick
    });
    manufacturerTab.manufacturers = transformedManufacturers;
    const { renderManufacturerItem } = manufacturerTab;

    const orderTab = OrderTab({
        orders: filteredResults.products?.filter(p => p.orderIntegrationId).map(p => ({
            id: p.id,
            productName: p.productName,
            productImage: p.productImage,
            price: p.price,
            orderDate: p.orderDate,
            orderStatus: p.orderStatus,
            orderIntegrationId: p.orderIntegrationId
        })) || [],
        isAllCategory: selectedFilterIndex === 0,
        onItemClick: handleItemClick
    });
    const { renderOrderItem } = orderTab;

    const bookmarksTab = BookmarksTab({
        searchResults: filteredResults,
        onBookmarkRemoved: () => {
            if (debouncedSearchQuery.trim()) {
                searchItems(debouncedSearchQuery.trim(), true);
            }
        }
    });
    const { renderBookmarksView } = bookmarksTab;

    const renderSection = (title, items, renderItem, sectionKey) => {
        if (!items || items.length === 0) return null;

        const displayItems = getDisplayItems(items, sectionKey);
        const hasMore = items.length > 5;
        const isExpanded = expandedSections[sectionKey];
        const isProductSection = sectionKey === 'products' && selectedFilterIndex === 1;
        const isAllCategory = selectedFilterIndex === 0;

        return (
            <div className='px-6 mt-4'>
                <div className='flex justify-between items-center'>
                    <div className='flex items-center'>
                        <BringzzText tag='h2'>{title}</BringzzText>
                        {isAllCategory && (
                            <div className='ml-2 bg-black text-white px-1.5 py-0.5 rounded-full text-xs min-w-[20px] text-center'>
                                {sectionKey === 'products' ? filteredResults.productCount || 0 :
                                    sectionKey === 'pharmacies' ? filteredResults.pharmacyCount || 0 :
                                        sectionKey === 'posts' ? filteredResults.postCount || 0 :
                                            sectionKey === 'companies' ? filteredResults.companyCount || 0 : items.length}
                            </div>
                        )}
                    </div>
                    {hasMore && (
                        <BringzzText
                            tag='h4'
                            className='text-magic-lilac cursor-pointer'
                            onClick={() => toggleSection(sectionKey)}
                        >
                            {isExpanded ? t('component.search.general.show_less') : t('component.search.general.show_all')}
                        </BringzzText>
                    )}
                </div>
                <div className={isProductSection ? 'grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3' : 'mt-1'}>
                    {displayItems.map((item, index) =>
                        isProductSection ? (
                            <div key={`${sectionKey}-${selectedFilterIndex}-${item.id}-${index}`} className='relative'
                                ref={index === displayItems.length - 1 ? lastElementRef : null}>
                                {renderItem(item, index, sectionKey)}
                            </div>
                        ) : (
                            <div key={`${sectionKey}-${selectedFilterIndex}-${item.id}-${index}`}
                                ref={index === displayItems.length - 1 ? lastElementRef : null}>
                                {renderItem(item, index, sectionKey)}
                            </div>
                        )
                    )}
                </div>
            </div>
        );
    };

    const footerContent = (
        <div className='pt-2 px-4 pb-4 shadow-top bg-white'>
            <div className='relative' ref={searchContainerRef}>
                <BringzzIcon
                    size="20"
                    icon="IconSearchAlt"
                    folder="LineIcons"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer"
                />
                <input
                    type="text"
                    placeholder={isCompanyMode ? `${t('component.search.general.products_from')} ${companyName}` : t('component.search.general.search_placeholder_full')}
                    value={searchQuery}
                    onChange={(e) => !isCompanyMode && setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' && !isCompanyMode) {
                            handleSearchSubmit();
                        }
                    }}
                    onFocus={() => {
                        if (!isCompanyMode && searchQuery.trim() && searchQuery.length >= 2) {
                            fetchSuggestions(searchQuery);
                        } else if (!isCompanyMode && searchSuggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    className={`w-full pl-10 pr-4 py-3 border border-slate-400 rounded-lg shadow-xl focus:outline-none focus:ring-2 focus:ring-magic-lilac focus:border-transparent ${isCompanyMode ? 'bg-gray-200 cursor-not-allowed' : 'bg-gray-100'}`}
                    readOnly={isCompanyMode}
                />
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 md:ml-64 overflow-y-auto">
            <div className="flex items-start justify-center min-h-screen">
                <div className="w-full md:max-w-xl md:mx-4">
                    <BringzzPageContainer
                        className="bg-white md:rounded-lg md:shadow-lg"
                        authenticated={false}
                        footerHover={false}
                        header={{
                            left: (
                                <BringzzIcon
                                    size="22"
                                    icon="IconClose"
                                    folder="LineIcons"
                                    className="cursor-pointer"
                                    onClick={() => {
                                        setIsCompanyMode(false);
                                        setCompanyProducts([]);
                                        setCurrentCompanyId(null);
                                        setSearchQuery('');
                                        setFilteredResults({});
                                        onClose();
                                    }}
                                />
                            ),
                            title: isCompanyMode ? companyName : t('component.search.general.search')
                        }}
                        footer={footerContent}
                    >

                        <div className='relative'>
                            <div className='pb-4'>
                                <div className='pl-6'>
                                    <BringzzBoxSlider className='pr-4 space-x-2'>
                                        {filterTypes.map((type, index) => (
                                            <div
                                                key={index}
                                                className={classNames('bg-gray-100 rounded-2xl py-2 px-4 cursor-pointer', index === selectedFilterIndex && "!bg-magic-lilac")}
                                                onClick={() => setSelectedFilterIndex(index)}
                                            >
                                                <BringzzText tag='h4'>{type}</BringzzText>
                                            </div>
                                        ))}
                                    </BringzzBoxSlider>
                                </div>

                                {selectedFilterIndex === 5 && ( // Bookmarks
                                    renderBookmarksView()
                                )}

                                {(Object.keys(filteredResults).length > 0 || isCompanyMode) && selectedFilterIndex !== 5 && !showSuggestions && (
                                    <div className='mt-4'>
                                        {selectedFilterIndex === 0 && ( // All
                                            <>
                                                {renderSection(t('component.search.general.products'), productTab.products, renderProductItem, 'products')}
                                                {renderSection(t('component.search.general.pharmacies'), pharmacyTab.pharmacies, renderPharmacyItem, 'pharmacies')}
                                                {renderSection(t('component.search.general.articles'), articleTab.posts, renderPostItem, 'posts')}
                                                {renderSection(t('component.search.general.manufacturers'), manufacturerTab.manufacturers, renderManufacturerItem, 'companies')}
                                            </>
                                        )}
                                        {selectedFilterIndex === 1 && ( // Product
                                            <div className='px-6 mt-4'>
                                                {isCompanyMode ? (
                                                    companyProducts.length > 0 ? (
                                                        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
                                                            {companyProducts.map((product, index) => {
                                                                const item = {
                                                                    id: product.id,
                                                                    name: product.productName,
                                                                    image: product.productImage,
                                                                    price: product.price ? `€${product.price}` : null,
                                                                    marked: product.isBookmarked,
                                                                    integrationId: product.integrationId
                                                                };
                                                                return (
                                                                    <div key={`company-product-${selectedFilterIndex}-${item.id}-${index}`} className='relative'
                                                                        ref={index === companyProducts.length - 1 ? lastElementRef : null}>
                                                                        {renderProductItem(item, index, 'products')}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className='text-center py-8'>
                                                            <BringzzText tag='h3' className='text-gray-500'>
                                                                {t('component.search.general.no_products_company')}
                                                            </BringzzText>
                                                        </div>
                                                    )
                                                ) : (
                                                    filteredResults.products?.length > 0 ? (
                                                        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
                                                            {filteredResults.products?.map((product, index) => {
                                                                const item = {
                                                                    id: product.id,
                                                                    name: product.groupName || product.productName,
                                                                    image: product.productImage,
                                                                    price: product.price ? `€${product.price}` : null,
                                                                    marked: product.isBookmarked,
                                                                    integrationId: product.integrationId,
                                                                    formLabel: product.formLabel || '',
                                                                    variantCount: product.variantCount || 1
                                                                };
                                                                return (
                                                                    <div key={`product-${selectedFilterIndex}-${item.id}-${index}`} className='relative'
                                                                        ref={index === filteredResults.products.length - 1 ? lastElementRef : null}>
                                                                        {renderProductItem(item, index, 'products')}
                                                                    </div>
                                                                );
                                                            })
                                                            }
                                                        </div>
                                                    ) : (
                                                        <div className='text-center py-8'>
                                                            <BringzzText tag='h3' className='text-gray-500'>
                                                                {t('component.search.general.no_results_found')}
                                                            </BringzzText>
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                        )}
                                        {selectedFilterIndex === 3 && ( // Pharmacies
                                            <div className='px-6 mt-4'>
                                                {filteredResults.pharmacies?.length > 0 ? (
                                                    filteredResults.pharmacies?.map((pharmacy, index) => {
                                                        const item = {
                                                            id: pharmacy.id,
                                                            name: pharmacy.name,
                                                            telefon: pharmacy.telefon,
                                                            image: pharmacy.pharmacyPic,
                                                            city: pharmacy.city,
                                                            marked: pharmacy.isBookmarked,
                                                            integrationId: pharmacy.integrationId
                                                        };
                                                        return (
                                                            <div key={`pharmacy-${selectedFilterIndex}-${item.id}-${index}`}
                                                                ref={index === filteredResults.pharmacies.length - 1 ? lastElementRef : null}>
                                                                {renderPharmacyItem(item, index, 'pharmacies')}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className='text-center py-8'>
                                                        <BringzzText tag='h3' className='text-gray-500'>
                                                            {t('component.search.general.no_results_found')}
                                                        </BringzzText>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {selectedFilterIndex === 2 && ( // Article
                                            <div className='px-6 mt-4'>
                                                {filteredResults.posts?.length > 0 ? (
                                                    filteredResults.posts?.map((post, index) => {
                                                        const item = {
                                                            id: post.id,
                                                            title: post.headline,
                                                            author: `${post.user_first_name || ''} ${post.user_last_name || ''}`.trim(),
                                                            authorImage: post.user_pic,
                                                            postImage: post.images?.[0]?.content,
                                                            integrationId: post.integrationId
                                                        };
                                                        return (
                                                            <div key={`post-${selectedFilterIndex}-${item.id}-${index}`}
                                                                ref={index === filteredResults.posts.length - 1 ? lastElementRef : null}>
                                                                {renderPostItem(item, index, 'posts')}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className='text-center py-8'>
                                                        <BringzzText tag='h3' className='text-gray-500'>
                                                            {t('component.search.general.no_results_found')}
                                                        </BringzzText>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {selectedFilterIndex === 4 && ( // Manufacturer
                                            <div className='px-6 mt-4'>
                                                {filteredResults.companies?.length > 0 ? (
                                                    filteredResults.companies?.map((company, index) => {
                                                        const item = {
                                                            id: company.id,
                                                            legal_name: company.legalName
                                                        };
                                                        return (
                                                            <div key={`manufacturer-${selectedFilterIndex}-${item.id}-${index}`}
                                                                className='h-12 flex justify-between px-0 items-center cursor-pointer hover:bg-gray-50 border-b'
                                                                ref={index === filteredResults.companies.length - 1 ? lastElementRef : null}
                                                                onClick={() => handleManufacturerProductsClick(item.id, item.legal_name)}>
                                                                <div className='flex space-x-1 items-center'>
                                                                    <BringzzText tag='h4'>{item.legal_name}</BringzzText>
                                                                </div>
                                                                <BringzzIcon
                                                                    size="16"
                                                                    icon="IconChevronRight"
                                                                    folder="LineIcons"
                                                                    className="text-gray-400"
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className='text-center py-8'>
                                                        <BringzzText tag='h3' className='text-gray-500'>
                                                            {t('component.search.general.no_results_found')}
                                                        </BringzzText>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {selectedFilterIndex === 6 && ( // Order
                                            <div className='px-6 mt-4'>
                                                {filteredResults.products?.filter(p => p.orderIntegrationId).length > 0 ? (
                                                    filteredResults.products?.filter(p => p.orderIntegrationId).map((product, index) => {
                                                        const item = {
                                                            id: product.id,
                                                            productName: product.productName,
                                                            productImage: product.productImage,
                                                            price: product.price,
                                                            orderDate: product.orderDate,
                                                            orderStatus: product.orderStatus,
                                                            orderIntegrationId: product.orderIntegrationId
                                                        };
                                                        return (
                                                            <div key={`order-${selectedFilterIndex}-${item.id}-${index}`}
                                                                ref={index === filteredResults.products.filter(p => p.orderIntegrationId).length - 1 ? lastElementRef : null}>
                                                                {renderOrderItem(item, index, 'orders')}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className='text-center py-8'>
                                                        <BringzzText tag='h3' className='text-gray-500'>
                                                            {t('component.search.general.no_orders_found')}
                                                        </BringzzText>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {Object.keys(filteredResults).every(key => !filteredResults[key] || filteredResults[key].length === 0) && !loading && !isCompanyMode && (
                                            <div className='px-6 mt-8 text-center'>
                                                <BringzzText tag='h3' className='text-gray-500'>
                                                    {t('component.search.general.no_results_found')}
                                                </BringzzText>
                                            </div>
                                        )}

                                        {(loading || isLoadingMore) && (
                                            <div className="flex items-center justify-center p-4 mb-8">
                                                <BringzzIcon
                                                    size="24"
                                                    icon="IconSpinner"
                                                    folder="LineIcons"
                                                    className="animate-spin text-magic-lilac"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {searchQuery.trim() && showSuggestions && (
                                    <div className='px-6 mt-4 suggestions-container'>
                                        <div
                                            className='flex items-center py-3 hover:bg-gray-50 cursor-pointer border-b'
                                            onClick={() => handleSuggestionClick(searchQuery.trim())}
                                        >
                                            <BringzzIcon
                                                size="16"
                                                icon="IconSearch"
                                                folder="LineIcons"
                                                className="text-gray-400 mr-3 flex-shrink-0"
                                            />
                                            <BringzzText tag='h4' className='flex-grow'>{searchQuery.trim()}</BringzzText>
                                        </div>
                                        {searchSuggestions.slice(1).map((suggestion, index) => (
                                            <div
                                                key={index}
                                                className='flex items-center py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0'
                                                onClick={() => handleSuggestionClick(suggestion)}
                                            >
                                                <BringzzIcon
                                                    size="16"
                                                    icon="IconSearch"
                                                    folder="LineIcons"
                                                    className="text-gray-400 mr-3 flex-shrink-0"
                                                />
                                                <BringzzText tag='h4' className='flex-grow'>{suggestion}</BringzzText>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!searchQuery.trim() && selectedFilterIndex !== 5 && ( // Not Bookmarks
                                    <div>
                                        {showHistory && searchHistory.length > 0 && (
                                            <div className='px-6 mt-4'>
                                                <div className='flex justify-between items-center mb-3'>
                                                    <BringzzText tag='h2'>{t('component.search.general.recent_searches')}</BringzzText>
                                                    <BringzzText
                                                        tag='h4'
                                                        className='text-magic-lilac cursor-pointer'
                                                        onClick={clearSearchHistory}
                                                    >
                                                        {t('component.search.general.clear_all')}
                                                    </BringzzText>
                                                </div>
                                                {(searchHistory || []).slice(0, 10).map((item, index) => (
                                                    <div
                                                        key={index}
                                                        className='flex items-center py-2 border-b'
                                                    >
                                                        <BringzzIcon
                                                            size="16"
                                                            icon="IconClock"
                                                            folder="LineIcons"
                                                            className="text-gray-400 mr-3"
                                                        />
                                                        <div
                                                            className='flex-grow cursor-pointer'
                                                            onClick={() => {
                                                                setSearchQuery(item.textSearch);
                                                                setShowHistory(false);
                                                                setShowSuggestions(false);
                                                                performSearch(item.textSearch);
                                                            }}
                                                        >
                                                            <BringzzText tag='h4'>{item.textSearch}</BringzzText>
                                                            <BringzzText tag='h5' className='text-gray-400'>
                                                                {new Date(item.dateTime).toLocaleDateString()}
                                                            </BringzzText>
                                                        </div>
                                                        <BringzzIcon
                                                            size="16"
                                                            icon="IconTrashCan"
                                                            folder="LineIcons"
                                                            className="text-gray-400 cursor-pointer ml-2"
                                                            onClick={() => deleteSearchHistory(item.integrationId)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {(!searchHistory || searchHistory.length === 0) && (
                                            <div className='px-6 mt-8 text-center'>
                                                <div className="flex justify-center">
                                                    <BringzzIcon
                                                        size="48"
                                                        icon="IconSearch"
                                                        folder="LineIcons"
                                                        className="text-gray-300 mb-4"
                                                    />
                                                </div>
                                                <BringzzText tag='h3' className='text-gray-500 mb-2'>
                                                    {t('component.search.general.start_typing_to_search')}
                                                </BringzzText>
                                                <BringzzText tag='h4' className='text-gray-400'>
                                                    {t('component.search.general.search_description')}
                                                </BringzzText>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </BringzzPageContainer>
                </div>
            </div>
        </div>
    );
};

export default SearchModal;